import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import QRCode from 'qrcode';
import { Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Backdrop, CircularProgress, Button, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardHeader, CardContent, Divider, ToggleButtonGroup, ToggleButton, useTheme, TextField, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Checkbox, ListItemText, ListSubheader } from '@mui/material';
import { useParams } from 'react-router-dom';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger/Logger';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { RootState } from 'state/store';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import SendIcon from '@mui/icons-material/Send';

interface WorkerItem {
  workerID: string;
  phoneNumber: string;
  messageCapacityPerDay: number;
  signalStrength: number;
  operatorName: string;
  operatorId: string;
  networkState: string;
  packetServiceState: string;
  primaryPort: string;
  lastStatusUpdate: string | Date;
  modemModel: string;
  messageTrackerCount?: number;
  lastUpdateQuota?: string | Date;
  registeredBy: string;
  qrCodeString?: string;
  whatsappStatus?: string;
  [key: string]: any;
}

interface WorkerPoolDoc {
  _id: string;
  poolName: string;
  poolType?: string;
  workers?: WorkerItem[];
  [key: string]: any;
}

const AUTO_REFRESH_INTERVAL_SEC = 15;

/** Renders WhatsApp QR code for scanning - used when worker has qrCodeString / whatsappStatus QR_READY */
const WhatsAppQRSection: React.FC<{
  qrCodeString?: string;
  qrCodeTimestamp?: string | Date;
  countdown: number;
  onRefresh: () => void;
}> = ({ qrCodeString, qrCodeTimestamp, countdown, onRefresh }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!qrCodeString) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(qrCodeString, { width: 256, margin: 2 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [qrCodeString]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Scan with WhatsApp on your phone
      </Typography>
      {qrDataUrl ? (
        <Box component="img" src={qrDataUrl} alt="WhatsApp QR" sx={{ width: 256, height: 256 }} />
      ) : (
        <Box sx={{ width: 256, height: 256, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
          <Typography variant="caption">Loading QR...</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          size="small"
        >
          Refresh ({countdown}s)
        </Button>
      </Box>
      {qrCodeTimestamp && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          QR last updated: {new Date(qrCodeTimestamp).toLocaleString('en-GB')}
        </Typography>
      )}
    </Box>
  );
};

/** Renders logged-in state when WhatsApp status is READY - no QR, show phone number and Logout */
const WhatsAppLoggedInSection: React.FC<{
  phoneNumber: string;
  countdown: number;
  onRefresh: () => void;
  onLogout: () => void;
  logoutLoading?: boolean;
  logoutRequested?: boolean;
}> = ({ phoneNumber, countdown, onRefresh, onLogout, logoutLoading, logoutRequested }) => {
  const isLogoutInProgress = logoutRequested || logoutLoading;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Already logged in with
      </Typography>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {phoneNumber || '—'}
      </Typography>
      {isLogoutInProgress && (
        <Chip
          label="Logout in progress..."
          color="warning"
          size="small"
          sx={{ mb: 1 }}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          size="small"
        >
          Refresh ({countdown}s)
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          size="small"
          disabled={isLogoutInProgress}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );
};

export const Workers: React.FC = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [pool, setPool] = useState<WorkerPoolDoc | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerItem | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tiles'>('tiles');
  const theme = useTheme();
  const [search, setSearch] = useState<string>('');
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [editForm, setEditForm] = useState<{ messageCapacityPerDay?: number; activeDays: number[]; startTime?: string; endTime?: string; timezone?: string }>({ activeDays: [] });
  const [logoutLoading, setLogoutLoading] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_INTERVAL_SEC);
  const [sendMessageOpen, setSendMessageOpen] = useState<boolean>(false);
  const [sendMessageForm, setSendMessageForm] = useState<{ destinationNumber: string; messageBody: string; templateId: string }>({
    destinationNumber: '',
    messageBody: '',
    templateId: ''
  });
  const [sendMessageLoading, setSendMessageLoading] = useState<boolean>(false);

  const user = useSelector((state: RootState) => (state as any)?.authentication?.user);
  const companyId = user?.companyId || '';

  useEffect(() => {
    const fetchPool = async () => {
      if (!poolId) return;
      try {
        setLoading(true);
        const resp = await apiClient.get(`/iu/workerpool/${poolId}`);
        const data = (resp as any)?.data || resp;
        setPool(data);
      } catch (e: any) {
        Logger.error('Settings/WorkerPool/Workers', 'Failed to fetch worker pool: ' + (e?.message || ''));
      } finally {
        setLoading(false);
      }
    };
    fetchPool();
  }, [poolId, refreshKey]);

  /** Sync selectedWorker with fresh pool data when view dialog is open - ensures QR refresh updates the dialog */
  useEffect(() => {
    if (!viewOpen || !selectedWorker?.workerID || !pool?.workers) return;
    const match = pool.workers.find((w: WorkerItem) => w.workerID === selectedWorker.workerID);
    if (match) setSelectedWorker(match);
  }, [pool, viewOpen, selectedWorker?.workerID]);

  /** Auto-refresh countdown when view dialog is open and worker is WhatsApp */
  const isWhatsAppWorkerInView = viewOpen && selectedWorker && String(pool?.poolType || '').toLowerCase() === 'whatsapp';
  const waStatusInView = String((selectedWorker as any)?.whatsappStatus || '').toUpperCase();
  const showWhatsAppSection = isWhatsAppWorkerInView && (waStatusInView === 'QR_READY' || /^(READY|SUCCESS|CONNECTED|PAIRED|LINKED)$/i.test(waStatusInView));
  useEffect(() => {
    if (!showWhatsAppSection) return;
    const id = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          setRefreshKey((x) => x + 1);
          return AUTO_REFRESH_INTERVAL_SEC;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [showWhatsAppSection]);
  useEffect(() => {
    if (showWhatsAppSection) setCountdown(AUTO_REFRESH_INTERVAL_SEC);
  }, [refreshKey, showWhatsAppSection]);

  const handleLogout = async () => {
    if (!poolId || !selectedWorker || !(selectedWorker as any)?._id) return;
    try {
      setLogoutLoading(true);
      await apiClient.post(`/iu/workerpool/${poolId}/workers/update/${(selectedWorker as any)._id}`, {
        whatsappLogoutRequested: true
      });
      setRefreshKey((x) => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/Workers', 'Failed to request WhatsApp logout: ' + (e?.message || ''));
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleSendMessage = (w: WorkerItem) => {
    setSelectedWorker(w);
    setSendMessageForm({
      destinationNumber: pool?.countryCode ? String(pool.countryCode).trim() : '',
      messageBody: '',
      templateId: ''
    });
    setSendMessageOpen(true);
  };

  const isGsmPool = String(pool?.poolType || '').toLowerCase() === 'gsm';

  const confirmSendMessage = async () => {
    if (!poolId || !sendMessageForm.destinationNumber?.trim() || !sendMessageForm.messageBody?.trim()) return;
    try {
      setSendMessageLoading(true);
      const tpl = sendMessageForm.templateId?.trim();
      await apiClient.post(`/iu/workerpool/${poolId}/messaging-queue`, {
        destinationNumber: sendMessageForm.destinationNumber.trim(),
        messageBody: sendMessageForm.messageBody.trim(),
        companyId: companyId || undefined,
        ...(isGsmPool && tpl ? { templateId: tpl } : {})
      });
      setSendMessageOpen(false);
      setSendMessageForm({ destinationNumber: '', messageBody: '', templateId: '' });
      setSelectedWorker(null);
      setRefreshKey((x) => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/Workers', 'Failed to add test message: ' + (e?.message || ''));
    } finally {
      setSendMessageLoading(false);
    }
  };

  const breadcrumbItems = useMemo(() => {
    return [
      { itemPath: '/administration/worker-pool', itemLabel: 'WorkerPool' },
      { itemPath: `/administration/worker-pool/${poolId}/workers`, itemLabel: pool?.poolName || '...' }
    ];
  }, [pool?.poolName, poolId]);

  const workers: WorkerItem[] = Array.isArray(pool?.workers) ? pool!.workers! : [];
  const filteredWorkers = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return workers;
    return workers.filter(w => String(w.phoneNumber || '').toLowerCase().includes(term));
  }, [workers, search]);
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const timezones = useMemo<string[]>(() => {
    try {
      const anyIntl: any = Intl as any;
      if (typeof anyIntl.supportedValuesOf === 'function') {
        return (anyIntl.supportedValuesOf('timeZone') as string[]) || [];
      }
    } catch {}
    // Fallback subset if environment lacks supportedValuesOf
    return [
      'UTC','Europe/London','Europe/Paris','Europe/Berlin','Europe/Madrid','Europe/Rome',
      'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
      'Asia/Kolkata','Asia/Dubai','Asia/Singapore','Asia/Tokyo','Australia/Sydney'
    ];
  }, []);
  const [tzSearch, setTzSearch] = useState<string>('');
  const getTzOffsetLabel = (tz: string): string | null => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: '2-digit' }).formatToParts(new Date());
      const name = parts.find(p => p.type === 'timeZoneName')?.value || '';
      const m = name.match(/([GU]MT|UTC)([+-]\d{1,2})(?::?(\d{2}))?/i);
      if (m) {
        const sign = m[2]?.startsWith('-') ? '-' : '+';
        const hh = Math.abs(parseInt(m[2] || '0', 10)).toString().padStart(2, '0');
        const mm = (m[3] || '00').toString().padStart(2, '0');
        return `GMT${sign}${hh}:${mm}`;
      }
    } catch {}
    return null;
  };
  const formatTzDisplay = (tz: string): string => {
    const off = getTzOffsetLabel(tz);
    return off ? `(${off}) ${tz}` : tz;
  };
  const filteredTimezones = useMemo(() => {
    const term = (tzSearch || '').toLowerCase();
    return (timezones || []).filter(tz => {
      const label = formatTzDisplay(tz).toLowerCase();
      return !term || label.includes(term);
    });
  }, [timezones, tzSearch]);

  const isActive = (last?: string | Date): boolean => {
    if (!last) return false;
    const ts = new Date(last).getTime();
    return (Date.now() - ts) <= 2 * 60 * 1000; // within last 2 minutes
  };

  /** Resolve status badge for worker - handles both GSM and WhatsApp pool types */
  const renderStatusBadge = (
    worker: WorkerItem,
    poolType?: string,
    containedActive?: boolean
  ) => {
    const last = worker.lastStatusUpdate;
    const active = isActive(last);
    const poolIsWhatsApp = String(poolType || '').toLowerCase() === 'whatsapp';
    const waStatus = String((worker as any).whatsappStatus || '').toUpperCase();

    // WhatsApp-specific status logic
    if (poolIsWhatsApp) {
      if (waStatus === 'QR_READY') {
        if (!active) return <Chip label="OFFLINE" color="error" variant="outlined" size="small" />;
        return <Chip label="QR_READY" color="warning" variant={containedActive ? 'filled' : 'outlined'} size="small" />;
      }
      // Status after QR scan: READY, SUCCESS, CONNECTED, PAIRED, etc.
      const isOnlineStatus = /^(READY|SUCCESS|CONNECTED|PAIRED|LINKED)$/i.test(waStatus);
      if (isOnlineStatus && active) {
        return <Chip label="ONLINE" color="success" variant={containedActive ? 'filled' : 'outlined'} size="small" />;
      }
      if (!active) return <Chip label="OFFLINE" color="error" variant="outlined" size="small" />;
      return <Chip label={waStatus || 'INACTIVE'} variant="outlined" size="small" />;
    }

    // GSM worker logic (original)
    const signalStrength = worker.signalStrength;
    const hasSignal = Number(signalStrength || 0) > 0;
    if (active && hasSignal) {
      return <Chip label="ACTIVE" color="success" variant={containedActive ? 'filled' : 'outlined'} size="small" />;
    }
    if (!hasSignal && active) {
      return <Chip label="INACTIVE" variant="outlined" size="small" />;
    }
    if (!hasSignal && !active) {
      return <Chip label="OFFLINE" color="error" variant="outlined" size="small" />;
    }
    return <Chip label="INACTIVE" variant="outlined" size="small" />;
  };

  const renderSignalBars = (strengthCsq?: number) => {
    const csq = Math.max(0, Math.min(31, Number(strengthCsq || 0)));
    const levels = Math.round((csq / 31) * 5); // 0..5 bars
    const bars = new Array(5).fill(0).map((_, i) => i < levels);
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 16 }}>
        {bars.map((filled, idx) => (
          <Box
            key={idx}
            sx={{
              width: 4,
              height: 6 + idx * 2,
              borderRadius: 1,
              bgcolor: filled ? 'success.main' : 'grey.400',
              opacity: filled ? 1 : 0.5
            }}
          />
        ))}
      </Box>
    );
  };

  const renderQuotaGaugeNeedle = (used?: number, limit?: number, color?: string, width: number = 140) => {
    const safeUsed = Number(used || 0);
    const safeLimit = Number(limit || 0);
    const isUnlimited = !isFinite(safeLimit) || safeLimit <= 0;
    const percentage = !isUnlimited ? Math.min(100, Math.max(0, (safeUsed / safeLimit) * 100)) : 0;
    const height = 84;
    const cx = width / 2;
    const cy = height;
    const r = Math.min(64, width / 2 - 6);
    const startX = cx - r;
    const startY = cy;
    const endX = cx + r;
    const endY = cy;
    const theta = Math.PI * (1 - percentage / 100);
    const needleLen = r - 10;
    const needleX = cx + needleLen * Math.cos(theta);
    const needleY = cy - needleLen * Math.sin(theta);
    const lineY = height + 6;
    const svgH = height + 64;
    const strokeColor = color || theme.palette.info.main;
    return (
      <Box sx={{ width, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={width} height={svgH} viewBox={`0 0 ${width} ${svgH}`} aria-label="Quota Gauge">
          <path d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`} fill="none" stroke="rgba(128,128,128,0.3)" strokeWidth="8" pathLength={100} strokeDasharray={isUnlimited ? '6 8' : undefined} />
          {!isUnlimited && (
            <>
              <path d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`} fill="none" stroke={strokeColor} strokeWidth="8" pathLength={100} strokeDasharray={`${percentage} 100`} strokeLinecap="round" />
              <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={strokeColor} strokeWidth="3" strokeLinecap="round" />
              <circle cx={cx} cy={cy} r="4" fill={strokeColor} />
            </>
          )}
          <line x1={startX} y1={lineY} x2={endX} y2={lineY} stroke="rgba(128,128,128,0.3)" strokeWidth="2" strokeLinecap="round" />
          {!isUnlimited ? (
            <text x={cx} y={cy - r + 20} textAnchor="middle" fontSize="11" fontWeight="700" fill="currentColor">
              {`${Math.round(percentage)}%`}
            </text>
          ) : (
            <text x={cx} y={cy - r + 20} textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor">
              ∞
            </text>
          )}
          <text x={cx} y={lineY + 22} textAnchor="middle" fontSize="11" fill="gray">
            {`${safeUsed}/${isUnlimited ? 'Unlimited' : safeLimit}`}
          </text>
        </svg>
      </Box>
    );
  };

  const handleView = (w: WorkerItem) => {
    setSelectedWorker(w);
    setViewOpen(true);
  };

  const handleDelete = (w: WorkerItem) => {
    setSelectedWorker(w);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!poolId || !selectedWorker || !(selectedWorker as any)?._id) {
      setDeleteOpen(false);
      return;
    }
    try {
      await apiClient.delete(`/iu/workerpool/${poolId}/workers/delete/${(selectedWorker as any)._id}`);
      setDeleteOpen(false);
      setSelectedWorker(null);
      setRefreshKey(x => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/Workers', 'Failed to delete worker: ' + (e?.message || ''));
      setDeleteOpen(false);
    }
  };

  const workerToStartTime = (w: any): string => {
    if (typeof w?.startTime === 'string') {
      const s = w.startTime.trim();
      if (/^\d{1,2}:\d{1,2}$/.test(s)) {
        const [h, m] = s.split(':').map((x: string) => parseInt(x, 10) || 0);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    if (typeof w?.startHour === 'number') return String(w.startHour).padStart(2, '0') + ':00';
    return '';
  };
  const workerToEndTime = (w: any): string => {
    if (typeof w?.endTime === 'string') {
      const s = w.endTime.trim();
      if (/^\d{1,2}:\d{1,2}$/.test(s)) {
        const [h, m] = s.split(':').map((x: string) => parseInt(x, 10) || 0);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    if (typeof w?.endHour === 'number') return w.endHour === 23 ? '23:59' : String(w.endHour).padStart(2, '0') + ':59';
    return '';
  };
  /** Parse HH:mm to minutes since midnight (0-1440). Returns -1 if invalid. */
  const timeToMinutes = (t: string): number => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || '').trim());
    if (!m) return -1;
    const h = parseInt(m[1] || '0', 10);
    const min = parseInt(m[2] || '0', 10);
    if (h < 0 || h > 23 || min < 0 || min > 59) return -1;
    return h * 60 + min;
  };

  /** Parse HH:mm to percentage (0-100) of 24h day. 23:59 => 100 for full bar display. */
  const timeToBarPct = (t: string): number => {
    const parts = (t || '').trim().split(':');
    const h = parseInt(parts[0] || '0', 10) || 0;
    const m = parseInt(parts[1] || '0', 10) || 0;
    if (h === 23 && m === 59) return 100;
    const mins = Math.min(h * 60 + m, 24 * 60 - 1);
    return (mins / (24 * 60)) * 100;
  };

  const handleEdit = (w: WorkerItem) => {
    setSelectedWorker(w);
    setEditForm({
      messageCapacityPerDay: Number(w.messageCapacityPerDay || 0),
      activeDays: Array.isArray((w as any).activeDays) ? (w as any).activeDays : [],
      startTime: workerToStartTime(w),
      endTime: workerToEndTime(w),
      timezone: typeof (w as any).timezone === 'string' ? (w as any).timezone : ''
    });
    setEditOpen(true);
  };

  const confirmEdit = async () => {
    if (!poolId || !selectedWorker || !(selectedWorker as any)?._id) {
      setEditOpen(false);
      return;
    }
    try {
      await apiClient.post(`/iu/workerpool/${poolId}/workers/update/${(selectedWorker as any)._id}`, {
        messageCapacityPerDay: editForm.messageCapacityPerDay,
        activeDays: editForm.activeDays,
        startTime: editForm.startTime || undefined,
        endTime: editForm.endTime || undefined,
        timezone: editForm.timezone
      });
      setEditOpen(false);
      setSelectedWorker(null);
      setRefreshKey(x => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/Workers', 'Failed to update worker: ' + (e?.message || ''));
      setEditOpen(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by phone number"
            size="small"
            variant="outlined"
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setViewMode(v)}
            color="secondary"
          >
            <ToggleButton value="table" aria-label="table-view">
              <TableRowsIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="tiles" aria-label="tile-view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setRefreshKey(x => x + 1)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>{pool?.poolName || ''} - Workers</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
          <Backdrop sx={{ color: 'secondary.main', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      ) : viewMode === 'table' ? (
        <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Signal</TableCell>
                <TableCell>Operator Name</TableCell>
                <TableCell>Network Status</TableCell>
                <TableCell>Last Status Update</TableCell>
                <TableCell>Modem Model</TableCell>
                <TableCell>Registered By</TableCell>
                <TableCell>Quota</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No workers registered.</TableCell>
                </TableRow>
              ) : (
                filteredWorkers.map((w: WorkerItem, idx: number) => (
                  <TableRow key={w.workerID || idx}>
                    <TableCell>{w.phoneNumber}</TableCell>
                    <TableCell>{renderSignalBars(w.signalStrength)}</TableCell>
                    <TableCell>{w.operatorName}</TableCell>
                    <TableCell>{w.networkState}</TableCell>
                    <TableCell>{w.lastStatusUpdate ? new Date(w.lastStatusUpdate).toLocaleString('en-GB') : ''}</TableCell>
                    <TableCell>{w.modemModel}</TableCell>
                    <TableCell>{w.registeredBy}</TableCell>
                    <TableCell>{renderQuotaGaugeNeedle(w.messageTrackerCount, w.messageCapacityPerDay, theme.palette.secondary.main, 120)}</TableCell>
                    <TableCell>{renderStatusBadge(w, pool?.poolType)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="View">
                          <Button 
                            aria-label="view" 
                            color="secondary" 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleView(w)}
                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button 
                            aria-label="edit" 
                            color="secondary" 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleEdit(w)}
                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          >
                            <EditIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                        {['whatsapp', 'gsm'].includes(String(pool?.poolType || '').toLowerCase()) && (
                          <Tooltip title="Send Message">
                            <Button 
                              aria-label="send-message" 
                              color="secondary" 
                              variant="outlined" 
                              size="small" 
                              onClick={() => handleSendMessage(w)}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                            >
                              <SendIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <Button 
                            aria-label="delete" 
                            color="error" 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleDelete(w)}
                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {filteredWorkers.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center' }}>No workers registered.</Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredWorkers.map((w: WorkerItem, idx: number) => (
                <Grid key={w.workerID || idx} item xs={12} sm={6} md={4} lg={3}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                      titleTypographyProps={{ variant: 'subtitle1' }}
                      subheaderTypographyProps={{ variant: 'caption' }}
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', flexWrap: 'wrap' }}>
                            {String(pool?.poolType || '').toLowerCase() === 'whatsapp' &&
                            (w as any).qrCodeString &&
                            String((w as any).whatsappStatus || '').toUpperCase() === 'QR_READY' &&
                            String(w.phoneNumber || '').toLowerCase() === 'pending' ? (
                              <Chip label="pending" size="small" color="warning" variant="outlined" />
                            ) : (
                              <Typography variant="subtitle1" noWrap>{w.phoneNumber || '-'}</Typography>
                            )}
                          </Box>
                          {renderStatusBadge(w, pool?.poolType, true)}
                        </Box>
                      }
                      subheader={w.operatorName || ''}
                    />
                    <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
                        <Box sx={{ flex: '0 0 auto' }}>
                          {renderQuotaGaugeNeedle(w.messageTrackerCount, w.messageCapacityPerDay, theme.palette.secondary.main, 160)}
                        </Box>
                        <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Signal</Typography>
                          {renderSignalBars(w.signalStrength)}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {Math.round((Math.max(0, Math.min(31, Number(w.signalStrength || 0))) / 31) * 100)}%
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Network</Typography>
                          <Typography variant="body2" noWrap>{w.networkState || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Model</Typography>
                          <Typography variant="body2" noWrap>{w.modemModel || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Last Update</Typography>
                          <Typography variant="body2" noWrap>{w.lastStatusUpdate ? new Date(w.lastStatusUpdate).toLocaleString('en-GB') : ''}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Registered By</Typography>
                          <Typography variant="body2" noWrap>{w.registeredBy || '-'}</Typography>
                        </Box>
                      </Box>
                      {/* Schedule visualization (activeDays, startTime, endTime) */}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Schedule</Typography>
                        {/* Active days row */}
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                          {['S','M','T','W','T','F','S'].map((label, idx) => {
                            const active = Array.isArray((w as any)?.activeDays) ? ((w as any).activeDays as number[]).includes(idx) : false;
                            return (
                              <Chip
                                key={`d-${idx}`}
                                label={label}
                                size="small"
                                color="secondary"
                                variant={active ? 'filled' : 'outlined'}
                                sx={{
                                  minWidth: 22,
                                  height: 22,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  lineHeight: 1.4,
                                  ...(active ? {
                                    '& .MuiChip-label': { color: 'common.black' },
                                    color: 'common.black'
                                  } : {})
                                }}
                              />
                            );
                          })}
                        </Box>
                        {/* Active time bar */}
                        <Box sx={{ mt: 0.5, width: '100%', minWidth: 120 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">00:00</Typography>
                            <Typography variant="caption" color="text.secondary">23:59</Typography>
                          </Box>
                          <Box sx={{ position: 'relative', width: '100%', minWidth: 120, height: 12, bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300', borderRadius: 999, overflow: 'hidden' }}>
                            {(() => {
                              const st = workerToStartTime(w);
                              const et = workerToEndTime(w);
                              if (!st || !et) return null;
                              const startPct = timeToBarPct(st);
                              const endPct = timeToBarPct(et);
                              const leftPct = Math.max(0, Math.min(100, Math.min(startPct, endPct)));
                              const rightPct = Math.max(0, Math.min(100, Math.max(startPct, endPct)));
                              return (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    right: `${100 - rightPct}%`,
                                    top: 0,
                                    bottom: 0,
                                    borderRadius: 999,
                                    bgcolor: 'secondary.main',
                                    zIndex: 1
                                  }}
                                />
                              );
                            })()}
                          </Box>
                          {(() => {
                            const st = workerToStartTime(w);
                            const et = workerToEndTime(w);
                            if (!st || !et) return null;
                            const startPct = timeToBarPct(st);
                            const endPct = timeToBarPct(et);
                            const leftPct = Math.max(0, Math.min(100, Math.min(startPct, endPct)));
                            const rightPct = Math.max(0, Math.min(100, Math.max(startPct, endPct)));
                            return (
                              <Box sx={{ position: 'relative', width: '100%', minWidth: 120, height: 16, mt: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    transform: 'translateX(-50%)',
                                    bottom: 0,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {st}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    position: 'absolute',
                                    left: `${rightPct}%`,
                                    transform: 'translateX(-50%)',
                                    bottom: 0,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {et}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                    </CardContent>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, gap: 1, flexWrap: 'wrap' }}>
                      {['whatsapp', 'gsm'].includes(String(pool?.poolType || '').toLowerCase()) && (
                        <Tooltip title="Send Message">
                          <Button 
                            aria-label="send-message" 
                            color="secondary" 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleSendMessage(w)}
                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          >
                            <SendIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="View">
                        <Button 
                          aria-label="view" 
                          color="secondary" 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleView(w)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button 
                          aria-label="edit" 
                          color="secondary" 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleEdit(w)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                        >
                          <EditIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button 
                          aria-label="delete" 
                          color="error" 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleDelete(w)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Worker Details</DialogTitle>
        <DialogContent dividers>
          {selectedWorker ? (
            <>
              {/* WhatsApp section - QR when QR_READY, logged-in message when READY */}
              {showWhatsAppSection && (
                waStatusInView === 'QR_READY' ? (
                  <WhatsAppQRSection
                    qrCodeString={(selectedWorker as any).qrCodeString}
                    qrCodeTimestamp={(selectedWorker as any).qrCodeTimestamp}
                    countdown={countdown}
                    onRefresh={() => { setRefreshKey((x) => x + 1); setCountdown(AUTO_REFRESH_INTERVAL_SEC); }}
                  />
                ) : (
                  <WhatsAppLoggedInSection
                    phoneNumber={(selectedWorker as any).whatsappPhoneNumber || selectedWorker.phoneNumber}
                    countdown={countdown}
                    onRefresh={() => { setRefreshKey((x) => x + 1); setCountdown(AUTO_REFRESH_INTERVAL_SEC); }}
                    onLogout={handleLogout}
                    logoutLoading={logoutLoading}
                    logoutRequested={!!(selectedWorker as any).whatsappLogoutRequested}
                  />
                )
              )}
              <Box component="div" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                {Object.keys(selectedWorker)
                  .filter(k => k !== 'qrCodeString' && k !== 'qrCodeTimestamp')
                  .map((k) => {
                  const rawVal = (selectedWorker as any)[k];
                  let displayVal: string = '';
                  if (k === 'activeDays' && Array.isArray(rawVal)) {
                    const arr = (rawVal as number[]).slice().sort((a,b)=>a-b);
                    const names = arr.map(d => ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d] ?? String(d));
                    displayVal = names.join(', ');
                  } else if ((k === 'startTime' || k === 'endTime') && typeof rawVal === 'string') {
                    displayVal = rawVal;
                  } else if ((k === 'startHour' || k === 'endHour') && typeof rawVal === 'number') {
                    displayVal = k === 'endHour' && rawVal === 23 ? '23:59' : rawVal.toString().padStart(2, '0') + (k === 'endHour' ? ':59' : ':00');
                  } else if (rawVal === null || rawVal === undefined) {
                    displayVal = '';
                  } else {
                    displayVal = String(rawVal);
                  }
                  return (
                    <Box key={k} sx={{ wordBreak: 'break-word' }}>
                      <Typography variant="caption" color="text.secondary">{k}</Typography>
                      <Typography variant="body2">{displayVal}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Send Test Message Dialog */}
      <Dialog open={sendMessageOpen} onClose={() => setSendMessageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Test Message</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Destination Number (with country code)"
              value={sendMessageForm.destinationNumber}
              onChange={(e) => setSendMessageForm((f) => ({ ...f, destinationNumber: e.target.value }))}
              placeholder="e.g. +441234567890"
              fullWidth
              size="small"
            />
            <TextField
              label="Message Body"
              value={sendMessageForm.messageBody}
              onChange={(e) => setSendMessageForm((f) => ({ ...f, messageBody: e.target.value }))}
              placeholder="Enter your message..."
              fullWidth
              multiline
              rows={4}
              size="small"
            />
            {isGsmPool && (
              <TextField
                label="Template Id (Tata Tele)"
                value={sendMessageForm.templateId}
                onChange={(e) => setSendMessageForm((f) => ({ ...f, templateId: e.target.value }))}
                placeholder="e.g. 1607100000000370425"
                helperText="Optional. If empty, the GSM worker uses TATA_TELE_TEMPLATE_ID_DEFAULT from its environment."
                fullWidth
                size="small"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setSendMessageOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmSendMessage}
            disabled={sendMessageLoading || !sendMessageForm.destinationNumber?.trim() || !sendMessageForm.messageBody?.trim()}
          >
            {sendMessageLoading ? 'Sending...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Worker Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Worker</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Message Capacity / Day"
              type="number"
              value={editForm.messageCapacityPerDay ?? ''}
              onChange={(e) => setEditForm(prev => ({ ...prev, messageCapacityPerDay: Number(e.target.value) }))}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="active-days-label">Active Days</InputLabel>
              <Select
                labelId="active-days-label"
                multiple
                value={editForm.activeDays}
                onChange={(e) => {
                  const value = e.target.value as number[];
                  setEditForm(prev => ({ ...prev, activeDays: value }));
                }}
                input={<OutlinedInput label="Active Days" />}
                renderValue={(selected) => {
                  const arr = (selected as number[]).sort((a,b)=>a-b);
                  return arr.map(d => dayNames[d] ?? String(d)).join(', ');
                }}
              >
                {[0,1,2,3,4,5,6].map((d) => (
                  <MenuItem key={d} value={d}>
                    <Checkbox color="secondary" checked={editForm.activeDays.indexOf(d) > -1} />
                    <ListItemText primary={dayNames[d]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Start Time"
              type="time"
              value={editForm.startTime || '00:00'}
              onChange={(e) => setEditForm(prev => ({ ...prev, startTime: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label="End Time"
              type="time"
              value={editForm.endTime || '23:59'}
              onChange={(e) => setEditForm(prev => ({ ...prev, endTime: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="timezone-label">Timezone</InputLabel>
              <Select
                labelId="timezone-label"
                value={editForm.timezone || ''}
                label="Timezone"
                onChange={(e) => setEditForm(prev => ({ ...prev, timezone: String(e.target.value) }))}
                renderValue={(selected) => {
                  const tz = String(selected || '');
                  return formatTzDisplay(tz);
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 48 * 10, width: 360 } },
                  MenuListProps: { autoFocusItem: false }
                }}
              >
                <ListSubheader disableSticky>
                  <TextField
                    autoFocus
                    placeholder="Search timezone"
                    fullWidth
                    size="small"
                    value={tzSearch}
                    onChange={(e) => setTzSearch(e.target.value)}
                    onKeyDown={(e) => {
                      // prevent Select from capturing keystrokes
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      // also stop keyup propagation to avoid type-to-select behavior
                      e.stopPropagation();
                    }}
                    onFocus={(e) => {
                      // keep focus on search input
                      e.stopPropagation();
                    }}
                  />
                </ListSubheader>
                {filteredTimezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>{formatTzDisplay(tz)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={confirmEdit} color="success" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Worker</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Deleting the worker from the pool is not recoverable. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workers;


